import type { ReactNode } from 'react';
import { CheckIcon, CriticalIcon, InfoIcon, WarningIcon, CloseIcon } from './icons';

/** Severity of an {@link Alert}. */
export type AlertVariant = 'info' | 'success' | 'warning' | 'critical';

/** Props for {@link Alert}. */
export interface AlertProps {
  /** Severity — selects icon, colour and live-region politeness. */
  variant: AlertVariant;
  /** Short bold heading. */
  title: string;
  /** Optional body / detail. */
  children?: ReactNode;
  /** When provided, renders a dismiss button that calls this handler. */
  onDismiss?: () => void;
  /** Optional extra classes. */
  className?: string;
}

/** Per-variant icon, colour classes and ARIA live-region role. */
const VARIANT_CONFIG: Record<
  AlertVariant,
  {
    Icon: (props: { size?: number; className?: string }) => JSX.Element;
    role: 'status' | 'alert';
    classes: string;
    iconClass: string;
  }
> = {
  info: {
    Icon: InfoIcon,
    role: 'status',
    classes: 'border-brand-200 bg-brand-50 text-brand-900 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-100',
    iconClass: 'text-brand-600 dark:text-brand-300',
  },
  success: {
    Icon: CheckIcon,
    role: 'status',
    classes: 'border-green-200 bg-green-50 text-green-900 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-100',
    iconClass: 'text-status-healthy',
  },
  warning: {
    Icon: WarningIcon,
    role: 'alert',
    classes: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100',
    iconClass: 'text-status-warning',
  },
  critical: {
    Icon: CriticalIcon,
    role: 'alert',
    classes: 'border-red-200 bg-red-50 text-red-900 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-100',
    iconClass: 'text-status-critical',
  },
};

/**
 * Inline banner communicating a message with a severity.
 *
 * Accessibility (spec 08 §2, §7): severity is conveyed by an icon AND text, never
 * colour alone. `warning`/`critical` use `role="alert"` (assertive) so they are
 * announced immediately; `info`/`success` use `role="status"` (polite). The
 * optional dismiss control is a labelled `<button>` with a visible focus ring.
 */
export function Alert({ variant, title, children, onDismiss, className = '' }: AlertProps): JSX.Element {
  const { Icon, role, classes, iconClass } = VARIANT_CONFIG[variant];
  return (
    <div
      role={role}
      className={`flex items-start gap-3 rounded-lg border p-3.5 text-sm ${classes} ${className}`.trim()}
    >
      <Icon size={18} className={`mt-0.5 shrink-0 ${iconClass}`} />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{title}</p>
        {children ? <div className="mt-1 text-sm opacity-90">{children}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-m-1 rounded p-1 opacity-70 transition-opacity hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
        >
          <CloseIcon size={16} />
        </button>
      ) : null}
    </div>
  );
}
